import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from '@/components/ui/dialog';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {ProjectPayload} from "@shared/interfaces/projectPayload.interface.ts";
import { ProjectYearlyCosts } from '@shared/interfaces/projectYearlyCosts.interface';
import {Label} from "@/components/ui/label.tsx";
import {Switch} from "@/components/ui/switch.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import {Plus, X, Copy, HelpCircle, Calendar as CalendarIcon} from "lucide-react";
import {useToast} from "@/hooks/use-toast";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { apiRequest } from '@/lib/queryClient';
import FinancialMetricsTable from './FinancialMetricsTable';
import { ZERO_PROJECT_YEARLY_COSTS } from '@shared/constants/projectYearlyCosts';

interface Collaborator {
    name: string;
    email: string;
}

interface EditProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: () => void;
    editProject: ProjectPayload;
    setEditProject: Dispatch<SetStateAction<ProjectPayload>>;
    isSubmitting: boolean;
    projectData?: any; // Original project data with current NDA status
}


const EditProjectModal: React.FC<EditProjectModalProps> = ({
                                                               isOpen,
                                                               onClose,
                                                               onSubmit,
                                                               editProject,
                                                               setEditProject,
                                                               isSubmitting,
                                                               projectData
                                                           }) => {
    const { toast } = useToast();
    const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [signingUrl, setSigningUrl] = useState<string>('');
    const userPricingTouchedRef = useRef(false);
    const handleYearlyCostsChange = useCallback((costs: ProjectYearlyCosts) => {
        setEditProject(prev => ({
            ...prev,
            yearly_costs: costs,
        }));
    }, [setEditProject]);

    // Initialize collaborators on modal open
    useEffect(() => {
        if (isOpen && projectData) {
            userPricingTouchedRef.current = false;

            // Handle company collaborators (support legacy string list)
            let initialCollaborators: Collaborator[] = [];
            if (projectData.client_collaborators && Array.isArray(projectData.client_collaborators)) {
                initialCollaborators = projectData.client_collaborators;
            } else if (projectData.collaborators && Array.isArray(projectData.collaborators)) {
                initialCollaborators = projectData.collaborators
                    .filter((email: string) => email && email.includes('@'))
                    .map((email: string) => ({ name: '', email: email.trim() }));
            }
            setCollaborators(initialCollaborators);

            const pricingEnabledFromVA = projectData?.summary_data?.valueSummary?.pricingEnabled ?? projectData?.value_assessment_pricing_enabled ?? false;

            // Initialize form fields from project data
            setEditProject({
                ...editProject,
                // Backfill primary contact email from owner if missing
                primary_contact_email: editProject.primary_contact_email || projectData.owner_email,
                client_collaborators: initialCollaborators,
                show_financial_metrics: !!pricingEnabledFromVA,
                // Initialize Key Dates from existing project data
                assessment_scheduled_at: projectData.assessment_scheduled_at || editProject.assessment_scheduled_at || '',
                assessment_completed_at: projectData.assessment_completed_at || editProject.assessment_completed_at || '',
                presentation_scheduled_at: projectData.presentation_scheduled_at || editProject.presentation_scheduled_at || ''
            });
        }
    }, [isOpen, projectData]);

    // Fetch latest pricingEnabled from Value Assessment on modal open to avoid stale state
    useEffect(() => {
        if (!isOpen || !projectData?.id) return;
        let aborted = false;
        (async () => {
            try {
                // Use apiRequest to include Authorization header; append cache-buster
                const data = await apiRequest(`/api/value-assessment/${projectData.id}?t=${Date.now()}`);
                const latestPricingEnabled = data?.data?.summary_data?.valueSummary?.pricingEnabled ?? data?.project?.value_assessment_pricing_enabled ?? false;
                if (!aborted && !userPricingTouchedRef.current && ((!!editProject.show_financial_metrics) !== (!!latestPricingEnabled))) {
                    setEditProject({
                        ...editProject,
                        show_financial_metrics: !!latestPricingEnabled
                    });
                }
            } catch (e) {
                console.error('Failed to fetch latest Value Assessment data for pricingEnabled:', e);
            }
        })();
        return () => { aborted = true; };
    }, [isOpen, projectData?.id]);

    // Validation
    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!editProject.client_org?.trim()) {
            newErrors.client_org = 'Company name is required';
        }
        if (!editProject.name?.trim()) {
            newErrors.name = 'Project name is required';
        }

        if (!editProject.primary_contact_name?.trim()) {
            newErrors.primary_contact_name = 'Primary contact name is required';
        }

        if (!editProject.primary_contact_email?.trim()) {
            newErrors.primary_contact_email = 'Primary contact email is required';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(editProject.primary_contact_email)) {
                newErrors.primary_contact_email = 'Invalid email format';
            }
        }

        if (!editProject.salesforce_opportunity_id?.trim()) {
            newErrors.salesforce_opportunity_id = 'Salesforce Opportunity ID is required';
        } else {
            // Validate Salesforce ID: exactly 18 characters, alphanumeric only
            const salesforceIdRegex = /^[A-Za-z0-9]{18}$/;
            if (!salesforceIdRegex.test(editProject.salesforce_opportunity_id)) {
                newErrors.salesforce_opportunity_id = 'Must be exactly 18 alphanumeric characters (A-Z, a-z, 0-9)';
            }
        }

        if (!editProject.nda_status) {
            newErrors.nda_status = 'NDA status selection is required';
        }

        // Validate collaborator emails
        collaborators.forEach((collab, index) => {
            if (collab.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(collab.email)) {
                newErrors[`collaborator_${index}_email`] = 'Invalid email format';
            }
            if (collab.email && !collab.name.trim()) {
                newErrors[`collaborator_${index}_name`] = 'Name is required';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle collaborator management
    const addCollaborator = () => {
        setCollaborators([...collaborators, { name: '', email: '' }]);
    };

    const removeCollaborator = (index: number) => {
        const updated = collaborators.filter((_, i) => i !== index);
        setCollaborators(updated);
        setEditProject({ ...editProject, client_collaborators: updated });
    };

    const updateCollaborator = (index: number, field: 'name' | 'email', value: string) => {
        const updated = collaborators.map((collab, i) => 
            i === index ? { ...collab, [field]: value } : collab
        );
        setCollaborators(updated);
        setEditProject({ ...editProject, client_collaborators: updated });
    };

    // Enhanced submit handler
    const handleSubmit = async () => {
        if (!validateForm()) {
            toast({
                title: "Validation Error",
                description: "Please fix the errors below",
                variant: "destructive"
            });
            return;
        }

        try {
            // Call original submit (which should now handle new fields)
            await onSubmit();

            // Persist pricingEnabled flag to value_assessment_results.summary_data.valueSummary.pricingEnabled
            if (projectData?.id) {
                try {
                    const payload = {
                        data: {
                            summary_data: {
                                valueSummary: {
                                    pricingEnabled: !!editProject.show_financial_metrics,
                                    project: {
                                        assessmentScheduledAt: editProject.assessment_scheduled_at || null,
                                        assessmentCompletedAt: editProject.assessment_completed_at || null,
                                        presentationScheduledAt: editProject.presentation_scheduled_at || null
                                    }
                                }
                            }
                        }
                    };

                    console.info('valueSummary@PostProjectSave', payload.data.summary_data.valueSummary);

                    const postValueAssessment = async () => {
                        return apiRequest(`/api/value-assessment/${projectData.id}`, {
                            method: 'POST',
                            body: payload,
                        });
                    };

                    try {
                        await postValueAssessment();
                    } catch (err: any) {
                        if (String(err?.message || err).startsWith('404:')) {
                            await apiRequest(`/api/value-assessment/init/${projectData.id}`, { method: 'POST' });
                            await postValueAssessment();
                        } else {
                            throw err;
                        }
                    }
                } catch (persistErr) {
                    console.error('Error persisting pricingEnabled:', persistErr);
                    // Non-fatal: project update succeeded, but we inform the user
                    // toast({
                    //     title: "Warning",
                    //     description: "Project updated, but failed to save the financial metrics toggle",
                    //     variant: "destructive"
                    // });
                }
            }

            // If user selected "needs_nda" and we don't have pending/signed status, send NDA
            if (editProject.nda_status === 'needs_nda' && 
                projectData?.nda_status !== 'pending' && 
                projectData?.nda_status !== 'signed') {
                
                try {
                    const result = await apiRequest('/api/nda/send', {
                        method: 'POST',
                        body: { projectId: projectData?.id }
                    });
                    
                    if (result.ok) {
                        if (result.signingUrl) {
                            setSigningUrl(result.signingUrl);
                        }
                        toast({
                            title: "NDA Sent",
                            description: `NDA workflow initiated for ${editProject.primary_contact_email}`,
                        });
                    } else if (result.requiresManual) {
                        // toast({
                        //     title: "Manual NDA Required",
                        //     description: "Automated NDA not configured. Please handle manually.",
                        //     variant: "destructive"
                        // });
                    }
                } catch (ndaError) {
                    console.error('NDA send failed:', ndaError);
                    // toast({
                    //     title: "NDA Send Failed",
                    //     description: "Project updated but NDA automation failed",
                    //     variant: "destructive"
                    // });
                }
            }

        } catch (error) {
            console.error('Project update failed:', error);
            toast({
                title: "Update Failed",
                description: "Failed to update project",
                variant: "destructive"
            });
        }
    };

    // Get current NDA status for badge
    const getCurrentNDAStatus = () => {
        if (projectData?.nda_status === 'signed') return { label: 'Signed', variant: 'default' as const };
        if (projectData?.nda_status === 'pending') return { label: 'Pending NDA', variant: 'secondary' as const };
        return { label: 'Not Started', variant: 'outline' as const };
    };

    const copySigningUrl = () => {
        navigator.clipboard.writeText(signingUrl);
        toast({ title: "Copied", description: "NDA link copied to clipboard" });
    };

    const ndaStatus = getCurrentNDAStatus();

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[1120px] bg-white dark:bg-gray-800 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <DialogTitle className="text-gray-900 dark:text-white">Edit Project</DialogTitle>
                            <DialogDescription className="text-gray-600 dark:text-gray-400">
                                Update project details and settings
                            </DialogDescription>
                        </div>
                        <Badge variant={ndaStatus.variant} className="ml-4">
                            {ndaStatus.label}
                        </Badge>
                    </div>
                </DialogHeader>

                {/* Pending NDA Banner */}
                {projectData?.nda_status === 'pending' && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3 mb-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-amber-800 dark:text-amber-200">
                                NDA sent to {projectData.primary_contact_email}. We'll activate automatically once signed.
                            </p>
                            {signingUrl && (
                                <Button variant="outline" size="sm" onClick={copySigningUrl}>
                                    <Copy className="w-4 h-4 mr-1" />
                                    Copy NDA Link
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                <div className="grid gap-6 py-4">
                    {/* 0. Company Name */}
                    <div className="grid gap-2">
                        <Label htmlFor="edit-client" className="text-gray-700 dark:text-gray-300">
                            Company Name <span className="text-red-500">*</span>
                            </Label>
                        <Input
                            id="edit-client"
                            value={editProject.client_org || ''}
                            onChange={(e) => setEditProject({ ...editProject, client_org: e.target.value })}
                            placeholder="Company Name"
                            className="border-gray-200 dark:border-gray-600"
                        />
                        {errors.client_org && <p className="text-sm text-red-500">{errors.client_org}</p>}
                    </div>
                    {/* 1. Project Name */}
                    <div className="grid gap-2">
                        <Label htmlFor="edit-name" className="text-gray-700 dark:text-gray-300">
                            Project Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="edit-name"
                            value={editProject.name || ''}
                            onChange={(e) => setEditProject({...editProject, name: e.target.value})}
                            placeholder="Enter company name"
                            className={`border-gray-200 dark:border-gray-600 ${errors.name ? 'border-red-500' : ''}`}
                        />
                        {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
                    </div>

                    {/* 2. Description */}
                    <div className="grid gap-2">
                        <Label htmlFor="edit-description" className="text-gray-700 dark:text-gray-300">
                            Description <span className="text-sm text-gray-500">(0-500 chars)</span>
                        </Label>
                        <Textarea
                            id="edit-description"
                            value={editProject.description || ''}
                            onChange={(e) => setEditProject({...editProject, description: e.target.value})}
                            placeholder="Describe the project objectives and scope"
                            className="border-gray-200 dark:border-gray-600 min-h-[100px]"
                            maxLength={500}
                        />
                        <div className="text-xs text-gray-500 text-right">
                            {(editProject.description || '').length}/500
                        </div>
                    </div>



                    {/* 3. Primary Contact - Full Name */}
                    <div className="grid gap-2">
                        <Label htmlFor="edit-contact-name" className="text-gray-700 dark:text-gray-300">
                            Primary Contact – Full Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="edit-contact-name"
                            value={editProject.primary_contact_name || ''}
                            onChange={(e) => setEditProject({...editProject, primary_contact_name: e.target.value})}
                            placeholder="Enter primary contact name"
                            className={`border-gray-200 dark:border-gray-600 ${errors.primary_contact_name ? 'border-red-500' : ''}`}
                        />
                        {errors.primary_contact_name && <p className="text-sm text-red-500">{errors.primary_contact_name}</p>}
                    </div>

                    {/* 4. Primary Contact - Email */}
                    <div className="grid gap-2">
                        <Label htmlFor="edit-contact-email" className="text-gray-700 dark:text-gray-300">
                            Primary Contact – Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="edit-contact-email"
                            type="email"
                            value={editProject.primary_contact_email || ''}
                            onChange={(e) => setEditProject({...editProject, primary_contact_email: e.target.value})}
                            placeholder="Enter primary contact email"
                            className={`border-gray-200 dark:border-gray-600 ${errors.primary_contact_email ? 'border-red-500' : ''}`}
                        />
                        {errors.primary_contact_email && <p className="text-sm text-red-500">{errors.primary_contact_email}</p>}
                    </div>

                    {/* 5. Company Collaborators */}
                    <div className="grid gap-2">
                        <div className="flex items-center justify-between">
                            <Label className="text-gray-700 dark:text-gray-300">Company Collaborators</Label>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={addCollaborator}
                                className="text-xs"
                            >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Collaborator
                            </Button>
                        </div>
                        {collaborators.map((collab, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 p-3 border border-gray-200 dark:border-gray-600 rounded-md">
                                <div>
                                    <Input
                                        placeholder="Name *"
                                        value={collab.name}
                                        onChange={(e) => updateCollaborator(index, 'name', e.target.value)}
                                        className={`text-sm ${errors[`collaborator_${index}_name`] ? 'border-red-500' : ''}`}
                                    />
                                    {errors[`collaborator_${index}_name`] && (
                                        <p className="text-xs text-red-500 mt-1">{errors[`collaborator_${index}_name`]}</p>
                                    )}
                                </div>
                                <div>
                                    <Input
                                        placeholder="Email *"
                                        type="email"
                                        value={collab.email}
                                        onChange={(e) => updateCollaborator(index, 'email', e.target.value)}
                                        className={`text-sm ${errors[`collaborator_${index}_email`] ? 'border-red-500' : ''}`}
                                    />
                                    {errors[`collaborator_${index}_email`] && (
                                        <p className="text-xs text-red-500 mt-1">{errors[`collaborator_${index}_email`]}</p>
                                    )}
                                </div>
                                <div className="flex items-center">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeCollaborator(index)}
                                        className="text-red-500 hover:text-red-700 px-2"
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 6. Salesforce Opportunity ID */}
                    <div className="grid gap-2">
                        <div className="flex items-center gap-2">
                            <Label htmlFor="edit-salesforce-id" className="text-gray-700 dark:text-gray-300">
                                Salesforce Opportunity ID <span className="text-red-500">*</span>
                            </Label>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <HelpCircle className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-sm">
                                        <div className="text-sm space-y-1">
                                            <p className="font-medium">To find your Salesforce Opportunity ID:</p>
                                            <p>• In Salesforce Lightning: Open the Opportunity → the URL looks like /lightning/r/Opportunity/{'{ID}'}/view. Copy the {'{ID}'} ( 18 characters).</p>
                                            <p>• Paste the ID here. Letters and numbers only.</p>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <Input
                            id="edit-salesforce-id"
                            value={editProject.salesforce_opportunity_id || ''}
                            onChange={(e) => setEditProject({...editProject, salesforce_opportunity_id: e.target.value})}
                            placeholder="Enter 18-character Salesforce Opportunity ID"
                            className={`border-gray-200 dark:border-gray-600 ${errors.salesforce_opportunity_id ? 'border-red-500' : ''}`}
                            maxLength={18}
                        />
                        {errors.salesforce_opportunity_id && <p className="text-sm text-red-500">{errors.salesforce_opportunity_id}</p>}
                    </div>

                    {/* 7. NDA Status */}
                    <div className="grid gap-3">
                        <Label className="text-gray-700 dark:text-gray-300">
                            NDA Status <span className="text-red-500">*</span>
                        </Label>
                        <div className="grid grid-cols-2 gap-3">
                            <Button
                                type="button"
                                variant={editProject.nda_status === 'already_signed' ? 'default' : 'outline'}
                                onClick={() => setEditProject({...editProject, nda_status: 'already_signed'})}
                                className="text-sm"
                            >
                                Already signed
                            </Button>
                            <Button
                                type="button"
                                variant={editProject.nda_status === 'needs_nda' ? 'default' : 'outline'}
                                onClick={() => setEditProject({...editProject, nda_status: 'needs_nda'})}
                                className="text-sm"
                            >
                                Needs NDA
                            </Button>
                        </div>
                        {errors.nda_status && <p className="text-sm text-red-500">{errors.nda_status}</p>}

                        {/* NDA Reference/Link and Date (shown when "Already signed" is selected) */}
                        {editProject.nda_status === 'already_signed' && (
                            <div className="grid gap-3 ml-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                                <div className="grid gap-2">
                                    <Label htmlFor="nda-reference" className="text-sm text-gray-600 dark:text-gray-400">
                                        NDA Reference / Link <span className="text-xs text-gray-500">(optional)</span>
                                    </Label>
                                    <Input
                                        id="nda-reference"
                                        value={editProject.nda_reference || ''}
                                        onChange={(e) => setEditProject({...editProject, nda_reference: e.target.value})}
                                        placeholder="Enter NDA reference or link"
                                        className="text-sm"
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="nda-signed-date" className="text-sm text-gray-600 dark:text-gray-400">
                                        NDA Signed Date <span className="text-xs text-gray-500">(optional)</span>
                                    </Label>
                                    <Input
                                        id="nda-signed-date"
                                        type="date"
                                        value={editProject.nda_signed_date || ''}
                                        onChange={(e) => setEditProject({...editProject, nda_signed_date: e.target.value})}
                                        className="text-sm"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Needs NDA Helper */}
                        {editProject.nda_status === 'needs_nda' && (
                            <div className="ml-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                    We'll trigger an Ironclad NDA. Project will be created in Pending NDA state until it's signed.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* 8. Export Options */}
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-gray-700 dark:text-gray-300">Export Options</Label>
                            <p className="text-sm text-gray-500">Enable export functionality for this project</p>
                        </div>
                        <Switch
                            checked={editProject.export_options_enabled ?? true}
                            onCheckedChange={(checked) => setEditProject({...editProject, export_options_enabled: checked})}
                        />
                    </div>

                    {/* 9. Show Financial Metrics */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label htmlFor="showFinancialMetrics" className="text-gray-700 dark:text-gray-300">Show financial metrics</Label>
                                <p className="text-sm text-gray-500">When on, the Visualization Summary can display financial metrics</p>
                            </div>
                            <Switch
                                id="showFinancialMetrics"
                                checked={!!editProject.show_financial_metrics}
                                onCheckedChange={(checked) => { userPricingTouchedRef.current = true; setEditProject({...editProject, show_financial_metrics: checked}); }}
                            />
                        </div>

                        <FinancialMetricsTable
                            value={editProject.yearly_costs}
                            fallback={ZERO_PROJECT_YEARLY_COSTS}
                            onChange={handleYearlyCostsChange}
                        />
                    </div>

                    {/* 10. Key Dates */}
                    <div className="grid gap-4">
                        <Label className="text-gray-700 dark:text-gray-300 text-base font-medium">Key Dates</Label>
                        
                        {/* Schedule Date */}
                        <div className="grid gap-2">
                            <Label className="text-gray-700 dark:text-gray-300 text-sm">Schedule Date</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "justify-start text-left font-normal",
                                            !editProject.assessment_scheduled_at && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {editProject.assessment_scheduled_at 
                                            ? format(new Date(editProject.assessment_scheduled_at), "PPP")
                                            : "Select schedule date"
                                        }
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={editProject.assessment_scheduled_at ? new Date(editProject.assessment_scheduled_at) : undefined}
                                        onSelect={(date) => {
                                            if (date) {
                                                // Convert to UTC ISO-8601 string
                                                const utcDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
                                                setEditProject({
                                                    ...editProject, 
                                                    assessment_scheduled_at: utcDate.toISOString()
                                                });
                                            }
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Completed Date (Read-only) */}
                        <div className="grid gap-2">
                            <Label className="text-gray-700 dark:text-gray-300 text-sm">Completed Date</Label>
                            <Input
                                value={editProject.assessment_completed_at 
                                    ? format(new Date(editProject.assessment_completed_at), "PPP")
                                    : "—"
                                }
                                readOnly
                                className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                                placeholder="Auto-stamped when assessment completes"
                            />
                        </div>

                        {/* Presentation Date */}
                        <div className="grid gap-2">
                            <Label className="text-gray-700 dark:text-gray-300 text-sm">Presentation Date</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "justify-start text-left font-normal",
                                            !editProject.presentation_scheduled_at && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {editProject.presentation_scheduled_at 
                                            ? format(new Date(editProject.presentation_scheduled_at), "PPP")
                                            : "Select presentation date"
                                        }
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={editProject.presentation_scheduled_at ? new Date(editProject.presentation_scheduled_at) : undefined}
                                        onSelect={(date) => {
                                            if (date) {
                                                // Convert to UTC ISO-8601 string
                                                const utcDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
                                                setEditProject({
                                                    ...editProject, 
                                                    presentation_scheduled_at: utcDate.toISOString()
                                                });
                                            }
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Validation Warning */}
                        {editProject.presentation_scheduled_at && editProject.assessment_scheduled_at && 
                         new Date(editProject.presentation_scheduled_at) < new Date(editProject.assessment_scheduled_at) && (
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-2">
                                <p className="text-xs text-amber-800 dark:text-amber-200">
                                    ⚠️ Presentation date is before schedule date
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="flex flex-col gap-3">
                    {/* Signed NDA Banner */}
                    {projectData?.nda_status === 'signed' && (
                        <div className="bg-primary dark:bg-primaryHover border border-primary dark:border-primaryHover rounded-md p-3">
                            <p className="text-sm text-green-800 dark:text-forestBlack">
                                ✅ NDA is signed and project is ready for full access
                                {projectData.nda_signed_at && ` (signed on ${new Date(projectData.nda_signed_at).toLocaleDateString()})`}
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="button" 
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Updating...' : 'Update Project'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditProjectModal;
